import React, { Fragment, useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';

import { AsyncIntlProvider } from '../common/utils/locale';
import { useNavBoundaryContext } from '../controllers/nav-boundary-context';
import { useBoundaryMessages } from '../controllers/use-boundary-messages';
import { useFailureFlagEmitter } from '../controllers/use-failure-flag-emitter';
import { useMessageFlags } from '../controllers/use-message-flags';
import { useReportInteractionError } from '../controllers/use-report-interaction-error';

import { ErrorBoundaryUI } from './error-boundary-ui';
import type { NavErrorBoundaryProps } from './types';

export function NavErrorBoundary(props: NavErrorBoundaryProps): React.JSX.Element {
	const { intlMode = 'async' } = props;

	const IntlWrapper =
		intlMode === 'inherit' || fg('navx-4866-migrate-messages-from-nav-error-boundary')
			? Fragment
			: AsyncIntlProvider;

	return (
		<IntlWrapper>
			<FlagsProvider>
				<InnerErrorBoundary {...props} />
			</FlagsProvider>
		</IntlWrapper>
	);
}

function InnerErrorBoundary({
	disableAnalytics = false,
	disableSentry = false,
	errorActionUI,
	errorFallbackUI,
	errorMessages,
	errorMessageValues,
	interactionId,
	location = 'unknown',
	onError,
	showFlags,
	silent,
	children,
	sentryConfig,
	tags,
	ufoInteractionName,
}: NavErrorBoundaryProps) {
	const { header, description, values } = useBoundaryMessages({
		errorMessages,
		errorMessageValues,
		location,
	});

	const flags = useMessageFlags({ showFlags });
	const emitFailureFlag = useFailureFlagEmitter(flags);
	const reportInteractionError = useReportInteractionError({ interactionId, ufoInteractionName });

	const onErrorUIHandler = useCallback(
		(error: Error) => {
			emitFailureFlag({ header, description, values });
			reportInteractionError(error);
			onError?.(error);
		},
		[emitFailureFlag, reportInteractionError, header, description, values, onError],
	);

	const { handleError } = useNavBoundaryContext({
		disableAnalytics,
		disableSentry,
		interactionId,
		location,
		onError: onErrorUIHandler,
		sentryConfig,
		tags,
	});

	// When `silent` is true or `flags` are enabled, we don't want to show any UI on error
	const errorComponent =
		silent || flags ? (
			<></>
		) : (
			<ErrorBoundaryUI
				disableAnalytics={disableAnalytics}
				errorActionUI={errorActionUI}
				errorFallbackUI={errorFallbackUI}
				errorMessages={errorMessages}
				errorMessageValues={errorMessageValues}
				location={location}
			/>
		);

	return (
		<ErrorBoundary onError={handleError} fallback={errorComponent}>
			{children}
		</ErrorBoundary>
	);
}
